import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { GlobalState } from "@/Reducers/interface";
import {
	Template,
	TemplateFilterType,
	AddTemplateRequest,
	TemplateSidePanelEnum,
	AssignUserToSignatureBlock,
	TemplateOwnerType,
	TemplateListRequest,
	TemplatesCount
} from "@/Contracts/Templates";
import {
	getTemplates,
	addTemplate,
	deleteTemplate,
	getTemplateDetails,
	editTemplate,
	updateTemplateBlockList,
	updateSignatureAssignee
} from "../../Services/Templates";
import {
	BlocksActionTypes,
	GetBlockMapperRequestedAction,
	GetBlockMapperSuccessfulAction,
	CreateBlockRequestedAction,
	CreateBlockSuccessfulAction
} from "@/ProposalBuilder/store/actions/Blocks";
import {
	BlockMapper,
	Block,
	draggableIdToBlockTypeMapper,
	createNewBlock,
	BlockType,
	TextBlockData,
	BlockResponse
} from "@/Contracts/ProposalBuilder/Block";
import {
	UpdateBlockListSequenceRequest,
	BlockRepositionRequest,
	DocumentDetailsRequest,
	MergeTagVariable,
	UpdateMultipleBlockListSequenceRequest,
} from "@/Contracts/Documents";
import {
	DocumentsActionTypes,
	LOADING_BLOCK_PLACEHOLDER,
	UpdateCurrentDocPaymentSummarySuccessfulAction
} from "../Documents";
import { addBlock } from "@/ProposalBuilder/services/Block";
import { updateMergeTagsDisplayName } from "@/Utils/MergeTags";
import { proposalBuilderStore } from "@/ProposalBuilder/store";

export enum TemplatesActionTypes {
	SHOW_TEMPLATES_LOADER = "SHOW_TEMPLATES_LOADER",
	HIDE_TEMPLATES_LOADER = "HIDE_TEMPLATES_LOADER",

	SHOW_TEMPLATE_MODAL_LOADER = "SHOW_TEMPLATE_MODAL_LOADER",
	HIDE_TEMPLATE_MODAL_LOADER = "HIDE_TEMPLATE_MODAL_LOADER",

	GET_TEMPLATES_REQUESTED = "GET_TEMPLATES_REQUESTED",
	GET_TEMPLATES_SUCCESSFUL = "GET_TEMPLATES_SUCCESSFUL",
	GET_TEMPLATES_FAILED = "GET_TEMPLATES_FAILED",
	RESET_TEMPLATES_LIST = "RESET_TEMPLATES_LIST",

	GET_TEMPLATE_DETAILS_REQUESTED = "GET_TEMPLATE_DETAILS_REQUESTED",
	GET_TEMPLATE_DETAILS_SUCCESSFUL = "GET_TEMPLATE_DETAILS_SUCCESSFUL",
	GET_TEMPLATE_DETAILS_FAILED = "GET_TEMPLATE_DETAILS_FAILED",
	RESET_TEMPLATE_DETAILS = "RESET_TEMPLATE_DETAILS",

	UPDATE_TEMPLATE_DETAILS_REQUESTED = "UPDATE_TEMPLATE_DETAILS_REQUESTED",
	UPDATE_TEMPLATE_DETAILS_SUCCESSFUL = "UPDATE_TEMPLATE_DETAILS_SUCCESSFUL",
	UPDATE_TEMPLATE_DETAILS_FAILED = "UPDATE_TEMPLATE_DETAILS_FAILED",

	TEMPLATE_DETAILS_REQUESTED = "TEMPLATE_DETAILS_REQUESTED",
	TEMPLATE_DETAILS_SUCCESSFUL = "TEMPLATE_DETAILS_SUCCESSFUL",
	TEMPLATE_DETAILS_FAILED = "TEMPLATE_DETAILS_FAILED",
	TEMPLATE_DETAILS_UPDATED = "TEMPLATE_DETAILS_UPDATED",

	DELETE_TEMPLATE_REQUESTED = "DELETE_TEMPLATE_REQUESTED",
	DELETE_TEMPLATE_SUCCESSFUL = "DELETE_TEMPLATE_SUCCESSFUL",
	DELETE_TEMPLATE_FAILED = "DELETE_TEMPLATE_FAILED",

	ADD_TEMPLATE_REQUESTED = "ADD_TEMPLATE_REQUESTED",
	ADD_TEMPLATE_SUCCESSFUL = "ADD_TEMPLATE_SUCCESSFUL",
	ADD_TEMPLATE_FAILED = "ADD_TEMPLATE_FAILED",

	EDIT_TEMPLATE_REQUESTED = "EDIT_TEMPLATE_REQUESTED",
	EDIT_TEMPLATE_SUCCESSFUL = "EDIT_TEMPLATE_SUCCESSFUL",
	EDIT_TEMPLATE_FAILED = "EDIT_TEMPLATE_FAILED",

	ADD_TEMPLATE_DRAWER_OPENED = "ADD_TEMPLATE_DRAWER_OPENED",
	ADD_TEMPLATE_DRAWER_CLOSED = "ADD_TEMPLATE_DRAWER_CLOSED",

	EDIT_TEMPLATE_DRAWER_OPENED = "EDIT_TEMPLATE_DRAWER_OPENED",
	EDIT_TEMPLATE_DRAWER_CLOSED = "EDIT_TEMPLATE_DRAWER_CLOSED",

	CHANGE_TEMPLATE_ACTIVE_TAB = "CHANGE_TEMPLATE_ACTIVE_TAB",

	CREATE_TEMPLATE_FROM_DOCUMENT_REQUESTED = "CREATE_TEMPLATE_FROM_DOCUMENT_REQUESTED",
	CREATE_TEMPLATE_FROM_DOCUMENT_SUCCESSFUL = "CREATE_TEMPLATE_FROM_DOCUMENT_SUCCESSFUL",
	CREATE_TEMPLATE_FROM_DOCUMENT_FAILED = "CREATE_TEMPLATE_FROM_DOCUMENT_FAILED",

	OPEN_TEMPLATE_PREVIEW_MODAL = "OPEN_TEMPLATE_PREVIEW_MODAL",
	CLOSE_TEMPLATE_PREVIEW_MODAL = "CLOSE_TEMPLATE_PREVIEW_MODAL",

	SET_TEMPLATE_SIDE_PANEL = "SET_TEMPLATE_SIDE_PANEL",
	OPEN_TEMPLATE_SIDE_PANEL = "OPEN_TEMPLATE_SIDE_PANEL",
	CLOSE_TEMPLATE_SIDE_PANEL = "CLOSE_TEMPLATE_SIDE_PANEL",

	UPDATE_SIGNATURE_ASSIGNEE_REQUESTED = "UPDATE_SIGNATURE_ASSIGNEE_REQUESTED",
	UPDATE_SIGNATURE_ASSIGNEE_SUCCESSFUL = "UPDATE_SIGNATURE_ASSIGNEE_SUCCESSFUL",
	UPDATE_SIGNATURE_ASSIGNEE_FAILED = "UPDATE_SIGNATURE_ASSIGNEE_FAILED",

	// Merge tag actions
	UPDATE_MERGE_TAG_REQUESTED = "UPDATE_MERGE_TAG_REQUESTED",
	UPDATE_MERGE_TAG_SUCCESSFUL = "UPDATE_MERGE_TAG_SUCCESSFUL",
	UPDATE_MERGE_TAG_FAILED = "UPDATE_MERGE_TAG_FAILED",

	// Block list actions
	UPDATE_TEMPLATE_BLOCK_LIST = "UPDATE_TEMPLATE_BLOCK_LIST"
}

export type ShowTemplatesLoaderAction =
	Action<TemplatesActionTypes.SHOW_TEMPLATES_LOADER>;
export type HideTemplatesLoaderAction =
	Action<TemplatesActionTypes.HIDE_TEMPLATES_LOADER>;
export type GetTemplatesRequestedAction =
	Action<TemplatesActionTypes.GET_TEMPLATES_REQUESTED>;
export interface GetTemplatesSuccessfulAction
	extends Action<TemplatesActionTypes.GET_TEMPLATES_SUCCESSFUL> {
	readonly templateList: Template[];
	readonly templateCount: TemplatesCount;
}

export type UpdateTemplateDetailsRequestedAction =
	Action<TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_REQUESTED>;
export interface UpdateTemplateDetailsSuccessfulAction
	extends Action<TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL> {
	readonly currentActiveTemplate: Template;
}
export type UpdateTemplateDetailsFailedAction =
	Action<TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_FAILED>;

export type GetTemplatesFailedAction =
	Action<TemplatesActionTypes.GET_TEMPLATES_FAILED>;
export type ResetTemplatesListAction =
	Action<TemplatesActionTypes.RESET_TEMPLATES_LIST>;
export type TemplateDetailsRequestedAction =
	Action<TemplatesActionTypes.TEMPLATE_DETAILS_REQUESTED>;
export interface TemplateDetailsSuccessfulAction
	extends Action<TemplatesActionTypes.TEMPLATE_DETAILS_SUCCESSFUL> {
	readonly currentActiveTemplate: Template;
}
export type TemplateDetailsFailedAction =
	Action<TemplatesActionTypes.TEMPLATE_DETAILS_FAILED>;
export type TemplateDetailsUpdatedAction =
	Action<TemplatesActionTypes.TEMPLATE_DETAILS_UPDATED>;
export type DeleteTemplateRequestedAction =
	Action<TemplatesActionTypes.DELETE_TEMPLATE_REQUESTED>;
export type DeleteTemplateSuccessfulAction =
	Action<TemplatesActionTypes.DELETE_TEMPLATE_SUCCESSFUL>;
export type DeleteTemplateFailedAction =
	Action<TemplatesActionTypes.DELETE_TEMPLATE_FAILED>;
export type AddTemplateRequestedAction =
	Action<TemplatesActionTypes.ADD_TEMPLATE_REQUESTED>;
export interface AddTemplateSuccessfulAction
	extends Action<TemplatesActionTypes.ADD_TEMPLATE_SUCCESSFUL> {
	readonly template: Template;
}
export type AddTemplateFailedAction =
	Action<TemplatesActionTypes.ADD_TEMPLATE_FAILED>;
export type EditTemplateRequestedAction =
	Action<TemplatesActionTypes.EDIT_TEMPLATE_REQUESTED>;
export type EditTemplateSuccessfulAction =
	Action<TemplatesActionTypes.EDIT_TEMPLATE_SUCCESSFUL>;
export type EditTemplateFailedAction =
	Action<TemplatesActionTypes.EDIT_TEMPLATE_FAILED>;
export type AddTemplateDrawerOpenedAction =
	Action<TemplatesActionTypes.ADD_TEMPLATE_DRAWER_OPENED>;
export type AddTemplateDrawerClosedAction =
	Action<TemplatesActionTypes.ADD_TEMPLATE_DRAWER_CLOSED>;
export type EditTemplateDrawerOpenedAction =
	Action<TemplatesActionTypes.EDIT_TEMPLATE_DRAWER_OPENED>;
export type EditTemplateDrawerClosedAction =
	Action<TemplatesActionTypes.EDIT_TEMPLATE_DRAWER_CLOSED>;

export interface ChangeTemplateActiveTabAction
	extends Action<TemplatesActionTypes.CHANGE_TEMPLATE_ACTIVE_TAB> {
	readonly activeTab: TemplateFilterType;
}
export type CreateTemplateFromDocumentRequestedAction =
	Action<TemplatesActionTypes.CREATE_TEMPLATE_FROM_DOCUMENT_REQUESTED>;
export type CreateTemplateFromDocumentSuccessfulAction =
	Action<TemplatesActionTypes.CREATE_TEMPLATE_FROM_DOCUMENT_SUCCESSFUL>;
export type CreateTemplateFromDocumentFailedAction =
	Action<TemplatesActionTypes.CREATE_TEMPLATE_FROM_DOCUMENT_FAILED>;

export type OpenTemplatePreviewModalAction =
	Action<TemplatesActionTypes.OPEN_TEMPLATE_PREVIEW_MODAL>;
export type CloseTemplatePreviewModalAction =
	Action<TemplatesActionTypes.CLOSE_TEMPLATE_PREVIEW_MODAL>;

export type ShowTemplateModalLoaderAction =
	Action<TemplatesActionTypes.SHOW_TEMPLATE_MODAL_LOADER>;
export type HideTemplateModalLoaderAction =
	Action<TemplatesActionTypes.HIDE_TEMPLATE_MODAL_LOADER>;

export type GetTemplateDetailsRequestedAction =
	Action<TemplatesActionTypes.GET_TEMPLATE_DETAILS_REQUESTED>;
export interface GetTemplateDetailsSuccessfulAction
	extends Action<TemplatesActionTypes.GET_TEMPLATE_DETAILS_SUCCESSFUL> {
	readonly currentActiveTemplate: Template;
}
export type GetTemplateDetailsFailedAction =
	Action<TemplatesActionTypes.GET_TEMPLATE_DETAILS_FAILED>;
export type ResetTemplateDetailsAction =
	Action<TemplatesActionTypes.RESET_TEMPLATE_DETAILS>;

export interface SetTemplateSidePanelAction
	extends Action<TemplatesActionTypes.SET_TEMPLATE_SIDE_PANEL> {
	readonly templateSidePanel: TemplateSidePanelEnum;
}
export type UpdateMergeTagRequestedAction =
	Action<TemplatesActionTypes.UPDATE_MERGE_TAG_REQUESTED>;
export type UpdateMergeTagFailedAction =
	Action<TemplatesActionTypes.UPDATE_MERGE_TAG_FAILED>;
export interface UpdateMergeTagSuccessfulAction
	extends Action<TemplatesActionTypes.UPDATE_MERGE_TAG_SUCCESSFUL> {
	readonly customMergeTagVariables?: MergeTagVariable[];
}

export type UpdateSignatureAssigneeRequestedAction =
	Action<TemplatesActionTypes.UPDATE_SIGNATURE_ASSIGNEE_REQUESTED>;
export interface UpdateSignatureAssigneeSuccessfulAction
	extends Action<TemplatesActionTypes.UPDATE_SIGNATURE_ASSIGNEE_SUCCESSFUL> {
	readonly response: Template;
}
export type UpdateSignatureAssigneeFailedAction =
	Action<TemplatesActionTypes.UPDATE_SIGNATURE_ASSIGNEE_FAILED>;

export type OpenTemplateSidePaneAction =
	Action<TemplatesActionTypes.OPEN_TEMPLATE_SIDE_PANEL>;
export type CloseTemplateSidePaneAction =
	Action<TemplatesActionTypes.CLOSE_TEMPLATE_SIDE_PANEL>;

export type TemplatesActions =
	| ShowTemplatesLoaderAction
	| HideTemplatesLoaderAction
	| ShowTemplateModalLoaderAction
	| HideTemplateModalLoaderAction
	| GetTemplatesRequestedAction
	| GetTemplatesSuccessfulAction
	| GetTemplatesFailedAction
	| ResetTemplatesListAction
	| TemplateDetailsRequestedAction
	| TemplateDetailsSuccessfulAction
	| TemplateDetailsFailedAction
	| TemplateDetailsUpdatedAction
	| DeleteTemplateRequestedAction
	| DeleteTemplateSuccessfulAction
	| DeleteTemplateFailedAction
	| AddTemplateRequestedAction
	| AddTemplateSuccessfulAction
	| AddTemplateFailedAction
	| EditTemplateRequestedAction
	| EditTemplateSuccessfulAction
	| EditTemplateFailedAction
	| AddTemplateDrawerOpenedAction
	| AddTemplateDrawerClosedAction
	| EditTemplateDrawerOpenedAction
	| EditTemplateDrawerClosedAction
	| ChangeTemplateActiveTabAction
	| OpenTemplatePreviewModalAction
	| CloseTemplatePreviewModalAction
	| CreateTemplateFromDocumentRequestedAction
	| CreateTemplateFromDocumentSuccessfulAction
	| CreateTemplateFromDocumentFailedAction
	| GetTemplateDetailsRequestedAction
	| GetTemplateDetailsSuccessfulAction
	| GetTemplateDetailsFailedAction
	| ResetTemplateDetailsAction
	| UpdateTemplateDetailsRequestedAction
	| UpdateTemplateDetailsSuccessfulAction
	| UpdateTemplateDetailsFailedAction
	| SetTemplateSidePanelAction
	| UpdateMergeTagRequestedAction
	| UpdateMergeTagSuccessfulAction
	| UpdateMergeTagFailedAction
	| UpdateSignatureAssigneeRequestedAction
	| UpdateSignatureAssigneeSuccessfulAction
	| UpdateSignatureAssigneeFailedAction
	| OpenTemplateSidePaneAction
	| CloseTemplateSidePaneAction;

export interface TemplatesDispatch {
	showTemplatesLoader: ActionCreator<
		ThunkAction<
			ShowTemplatesLoaderAction,
			GlobalState,
			null,
			ShowTemplatesLoaderAction
		>
	>;
	hideTemplatesLoader: ActionCreator<
		ThunkAction<
			HideTemplatesLoaderAction,
			GlobalState,
			null,
			HideTemplatesLoaderAction
		>
	>;
	showTemplateModalLoader: ActionCreator<
		ThunkAction<
			ShowTemplateModalLoaderAction,
			GlobalState,
			null,
			ShowTemplateModalLoaderAction
		>
	>;
	hideTemplateModalLoader: ActionCreator<
		ThunkAction<
			HideTemplateModalLoaderAction,
			GlobalState,
			null,
			HideTemplateModalLoaderAction
		>
	>;
	openTemplatePreviewModal: ActionCreator<
		ThunkAction<
			OpenTemplatePreviewModalAction,
			GlobalState,
			null,
			OpenTemplatePreviewModalAction
		>
	>;
	closeTemplatePreviewModal: ActionCreator<
		ThunkAction<
			CloseTemplatePreviewModalAction,
			GlobalState,
			null,
			CloseTemplatePreviewModalAction
		>
	>;
	changeTemplateActiveTab: ActionCreator<
		ThunkAction<
			ChangeTemplateActiveTabAction,
			GlobalState,
			TemplateFilterType,
			ChangeTemplateActiveTabAction
		>
	>;
	getTemplates: ActionCreator<
		ThunkAction<
			Promise<GetTemplatesSuccessfulAction>,
			GlobalState,
			{
				templateListRequest: TemplateListRequest;
				templateOwnerType: TemplateOwnerType;
			},
			GetTemplatesSuccessfulAction
		>
	>;
	getTemplateDetails: ActionCreator<
		ThunkAction<
			Promise<GetTemplateDetailsSuccessfulAction>,
			GlobalState,
			DocumentDetailsRequest,
			GetTemplateDetailsSuccessfulAction
		>
	>;
	updateCurrentTemplateDetails: ActionCreator<
		ThunkAction<
			Promise<UpdateTemplateDetailsSuccessfulAction>,
			GlobalState,
			Template,
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	addBlockToList: ActionCreator<
		ThunkAction<
			Promise<UpdateTemplateDetailsSuccessfulAction>,
			GlobalState,
			UpdateBlockListSequenceRequest,
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	linkBlockListToTemplate: ActionCreator<
		ThunkAction<
			Promise<UpdateTemplateDetailsSuccessfulAction>,
			GlobalState,
			UpdateMultipleBlockListSequenceRequest,
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	deleteBlockFromRevision: ActionCreator<
		ThunkAction<
			Promise<UpdateTemplateDetailsSuccessfulAction>,
			GlobalState,
			{ blockIds: string[] },
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	repositionBlockInRevision: ActionCreator<
		ThunkAction<
			UpdateTemplateDetailsSuccessfulAction,
			GlobalState,
			UpdateBlockListSequenceRequest,
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	addTemplate: ActionCreator<
		ThunkAction<
			Promise<AddTemplateSuccessfulAction>,
			GlobalState,
			AddTemplateRequest,
			AddTemplateSuccessfulAction
		>
	>;
	deleteTemplate: ActionCreator<
		ThunkAction<
			Promise<DeleteTemplateSuccessfulAction>,
			GlobalState,
			{ templateId: string },
			DeleteTemplateSuccessfulAction
		>
	>;
	createTemplateFromDocument: ActionCreator<
		ThunkAction<
			Promise<CreateTemplateFromDocumentSuccessfulAction>,
			GlobalState,
			AddTemplateRequest,
			CreateTemplateFromDocumentSuccessfulAction
		>
	>;
	resetTemplateList: ActionCreator<
		ThunkAction<
			ResetTemplatesListAction,
			GlobalState,
			null,
			ResetTemplatesListAction
		>
	>;
	resetTemplateDetails: ActionCreator<
		ThunkAction<
			ResetTemplateDetailsAction,
			GlobalState,
			null,
			ResetTemplateDetailsAction
		>
	>;
	setSidePanel: ActionCreator<
		ThunkAction<
			SetTemplateSidePanelAction,
			GlobalState,
			TemplateSidePanelEnum,
			SetTemplateSidePanelAction
		>
	>;
	linkBlockToRevision: ActionCreator<
		ThunkAction<
			Promise<UpdateTemplateDetailsSuccessfulAction>,
			GlobalState,
			UpdateBlockListSequenceRequest,
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	updateMergeTags: ActionCreator<
		ThunkAction<
			Promise<UpdateMergeTagSuccessfulAction>,
			GlobalState,
			MergeTagVariable[],
			UpdateMergeTagSuccessfulAction
		>
	>;
	updateSignatureAssignee: ActionCreator<
		ThunkAction<
			Promise<UpdateSignatureAssigneeSuccessfulAction>,
			GlobalState,
			AssignUserToSignatureBlock,
			UpdateSignatureAssigneeSuccessfulAction
		>
	>;
	openTemplateSidePanel: ActionCreator<
		ThunkAction<
			OpenTemplateSidePaneAction,
			GlobalState,
			null,
			OpenTemplateSidePaneAction
		>
	>;
	closeTemplateSidePanel: ActionCreator<
		ThunkAction<
			CloseTemplateSidePaneAction,
			GlobalState,
			null,
			CloseTemplateSidePaneAction
		>
	>;
	updateTemplateDetails: ActionCreator<
		ThunkAction<
			UpdateTemplateDetailsSuccessfulAction,
			GlobalState,
			Template,
			UpdateTemplateDetailsSuccessfulAction
		>
	>;
	updateBlockList: ActionCreator<
		ThunkAction<
			Promise<void>,
			GlobalState,
			null,
			Action
		>
	>;
}

export const TemplatesActionCreators: TemplatesDispatch = {
	setSidePanel: panelTab => {
		return (dispatch: Dispatch): SetTemplateSidePanelAction => {
			const setTemplateSidePanelAction: SetTemplateSidePanelAction = {
				type: TemplatesActionTypes.SET_TEMPLATE_SIDE_PANEL,
				templateSidePanel: panelTab
			};
			return dispatch(setTemplateSidePanelAction);
		};
	},
	showTemplatesLoader: () => {
		return (dispatch: Dispatch): ShowTemplatesLoaderAction => {
			const showTemplatesLoaderAction: ShowTemplatesLoaderAction = {
				type: TemplatesActionTypes.SHOW_TEMPLATES_LOADER
			};
			return dispatch(showTemplatesLoaderAction);
		};
	},
	hideTemplatesLoader: () => {
		return (dispatch: Dispatch): HideTemplatesLoaderAction => {
			const hideTemplatesLoaderAction: HideTemplatesLoaderAction = {
				type: TemplatesActionTypes.HIDE_TEMPLATES_LOADER
			};
			return dispatch(hideTemplatesLoaderAction);
		};
	},
	showTemplateModalLoader: () => {
		return (dispatch: Dispatch): ShowTemplateModalLoaderAction => {
			const showTemplateModalLoaderAction: ShowTemplateModalLoaderAction =
				{
					type: TemplatesActionTypes.SHOW_TEMPLATE_MODAL_LOADER
				};
			return dispatch(showTemplateModalLoaderAction);
		};
	},
	hideTemplateModalLoader: () => {
		return (dispatch: Dispatch): HideTemplateModalLoaderAction => {
			const hideTemplateModalLoaderAction: HideTemplateModalLoaderAction =
				{
					type: TemplatesActionTypes.HIDE_TEMPLATE_MODAL_LOADER
				};
			return dispatch(hideTemplateModalLoaderAction);
		};
	},
	openTemplatePreviewModal: () => {
		return (dispatch: Dispatch): OpenTemplatePreviewModalAction => {
			const openTemplatePreviewModalAction: OpenTemplatePreviewModalAction =
				{
					type: TemplatesActionTypes.OPEN_TEMPLATE_PREVIEW_MODAL
				};
			return dispatch(openTemplatePreviewModalAction);
		};
	},
	closeTemplatePreviewModal: () => {
		return (dispatch: Dispatch): CloseTemplatePreviewModalAction => {
			const closeTemplatePreviewModalAction: CloseTemplatePreviewModalAction =
				{
					type: TemplatesActionTypes.CLOSE_TEMPLATE_PREVIEW_MODAL
				};
			return dispatch(closeTemplatePreviewModalAction);
		};
	},
	changeTemplateActiveTab: (activeTab: TemplateFilterType) => {
		return (dispatch: Dispatch): ChangeTemplateActiveTabAction => {
			const changeTemplateActiveTabAction: ChangeTemplateActiveTabAction =
				{
					type: TemplatesActionTypes.CHANGE_TEMPLATE_ACTIVE_TAB,
					activeTab
				};
			return dispatch(changeTemplateActiveTabAction);
		};
	},
	addTemplate:
		(templateRequest: AddTemplateRequest) =>
		async (dispatch: Dispatch): Promise<AddTemplateSuccessfulAction> => {
			try {
				const addTemplateRequestedAction: AddTemplateRequestedAction = {
					type: TemplatesActionTypes.ADD_TEMPLATE_REQUESTED
				};
				dispatch(addTemplateRequestedAction);
				const templateResponse = await addTemplate(templateRequest);
				const addTemplateSuccessfulAction: AddTemplateSuccessfulAction =
					{
						type: TemplatesActionTypes.ADD_TEMPLATE_SUCCESSFUL,
						template: templateResponse
					};
				return dispatch(addTemplateSuccessfulAction);
			} catch (error) {
				const addTemplateFailedAction: AddTemplateFailedAction = {
					type: TemplatesActionTypes.ADD_TEMPLATE_FAILED
				};
				dispatch(addTemplateFailedAction);

				return Promise.reject(error);
			}
		},
	getTemplates:
		(
			templateListRequest: TemplateListRequest,
			templateOwnerType: TemplateOwnerType
		) =>
		async (dispatch: Dispatch): Promise<GetTemplatesSuccessfulAction> => {
			const templatesRequestedAction: GetTemplatesRequestedAction = {
				type: TemplatesActionTypes.GET_TEMPLATES_REQUESTED
			};
			dispatch(templatesRequestedAction);

			try {
				const templatesResponse = await getTemplates(
					templateListRequest,
					templateOwnerType
				);
				const templatesSuccessfulAction: GetTemplatesSuccessfulAction =
					{
						type: TemplatesActionTypes.GET_TEMPLATES_SUCCESSFUL,
						templateList: templatesResponse.data,
						templateCount: templatesResponse.filtered_count_map
					};
				return dispatch(templatesSuccessfulAction);
			} catch (error) {
				const templatesFailedAction: GetTemplatesFailedAction = {
					type: TemplatesActionTypes.GET_TEMPLATES_FAILED
				};
				dispatch(templatesFailedAction);

				return Promise.reject(error);
			}
		},

	getTemplateDetails:
		(
			templateDetailsRequest: DocumentDetailsRequest,
			includeVendorEntities = false
		) =>
		async (
			dispatch: Dispatch
		): Promise<GetTemplateDetailsSuccessfulAction> => {
			const getTemplateDetailsRequestedAction: GetTemplateDetailsRequestedAction =
				{
					type: TemplatesActionTypes.GET_TEMPLATE_DETAILS_REQUESTED
				};
			dispatch(getTemplateDetailsRequestedAction);

			try {
				const template = await getTemplateDetails(
					templateDetailsRequest.documentId,
					includeVendorEntities
				);

				// requesting for the blocks
				const getBlockMapperRequestedAction: GetBlockMapperRequestedAction =
					{
						type: BlocksActionTypes.GET_BLOCK_MAPPER_REQUESTED
					};
				proposalBuilderStore.dispatch(getBlockMapperRequestedAction);

				const blockMapper: BlockMapper =
					(template.blocks &&
						template.blocks.data.reduce(
							(total: BlockMapper, block: Block) => ({
								...total,
								[block.id]: block
							}),
							{}
						)) ||
					{};

				const mergeTags = [
					...(template.merge_tags_system_variables ?? []),
					...(template.merge_tag_custom_variables ?? [])
				];

				Object.keys(blockMapper).forEach(blockId => {
					if (
						blockMapper[blockId]?.type === BlockType.TEXT &&
						mergeTags
					) {
						(blockMapper[blockId].data as TextBlockData).text =
							updateMergeTagsDisplayName(
								mergeTags,
								(blockMapper[blockId].data as TextBlockData)
									.text
							);
					}
				});

				// updating the blocks to the state
				const getBlockMapperSuccessfulAction: GetBlockMapperSuccessfulAction =
					{
						type: BlocksActionTypes.GET_BLOCK_MAPPER_SUCCESSFUL,
						blockMapper
					};
				proposalBuilderStore.dispatch(getBlockMapperSuccessfulAction);

				const getTemplateDetailsSuccessfulAction: GetTemplateDetailsSuccessfulAction =
					{
						type: TemplatesActionTypes.GET_TEMPLATE_DETAILS_SUCCESSFUL,
						currentActiveTemplate: template
					};
				return dispatch(getTemplateDetailsSuccessfulAction);
			} catch (error) {
				const getTemplateDetailsFailedAction: GetTemplateDetailsFailedAction =
					{
						type: TemplatesActionTypes.GET_TEMPLATE_DETAILS_FAILED
					};
				dispatch(getTemplateDetailsFailedAction);

				return Promise.reject(error);
			}
		},
	updateCurrentTemplateDetails:
		(currentActiveTemplate: Template) =>
		async (
			dispatch: Dispatch
		): Promise<UpdateTemplateDetailsSuccessfulAction> => {
			const updateCurrentDocumentRequestedAction: UpdateTemplateDetailsRequestedAction =
				{
					type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_REQUESTED
				};
			dispatch(updateCurrentDocumentRequestedAction);
			try {
				const template = await editTemplate(currentActiveTemplate);
				const updateCurrentDocumentSuccessfulAction: UpdateTemplateDetailsSuccessfulAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
						currentActiveTemplate: template
					};
				return dispatch(updateCurrentDocumentSuccessfulAction);
			} catch (error) {
				const updateCurrentDocumentFailedAction: UpdateTemplateDetailsFailedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_FAILED
					};
				dispatch(updateCurrentDocumentFailedAction);
				throw error;
			}
		},
	deleteTemplate:
		(templateId: string) =>
		async (dispatch: Dispatch): Promise<DeleteTemplateSuccessfulAction> => {
			const deleteTemplateRequestedAction: DeleteTemplateRequestedAction =
				{
					type: TemplatesActionTypes.DELETE_TEMPLATE_REQUESTED
				};
			dispatch(deleteTemplateRequestedAction);

			try {
				await deleteTemplate(templateId);
				const templatesSuccessfulAction: DeleteTemplateSuccessfulAction =
					{
						type: TemplatesActionTypes.DELETE_TEMPLATE_SUCCESSFUL
					};
				return dispatch(templatesSuccessfulAction);
			} catch (error) {
				const deleteTemplateFailedAction: DeleteTemplateFailedAction = {
					type: TemplatesActionTypes.DELETE_TEMPLATE_FAILED
				};
				dispatch(deleteTemplateFailedAction);

				return Promise.reject(error);
			}
		},
	createTemplateFromDocument:
		({
			name,
			document_id,
			qbr_representation_style,
			assessment_representation_style
		}: AddTemplateRequest) =>
		async (
			dispatch: Dispatch
		): Promise<CreateTemplateFromDocumentSuccessfulAction> => {
			const createTemplateFromDocumentRequestedAction: CreateTemplateFromDocumentRequestedAction =
				{
					type: TemplatesActionTypes.CREATE_TEMPLATE_FROM_DOCUMENT_REQUESTED
				};
			dispatch(createTemplateFromDocumentRequestedAction);

			try {
				await addTemplate({
					name,
					document_id,
					qbr_representation_style,
					assessment_representation_style
				});

				const createTemplateFromDocumentSuccessfulAction: CreateTemplateFromDocumentSuccessfulAction =
					{
						type: TemplatesActionTypes.CREATE_TEMPLATE_FROM_DOCUMENT_SUCCESSFUL
					};

				return dispatch(createTemplateFromDocumentSuccessfulAction);
			} catch (error) {
				const createTemplateFromDocumentFailedAction: CreateTemplateFromDocumentFailedAction =
					{
						type: TemplatesActionTypes.CREATE_TEMPLATE_FROM_DOCUMENT_FAILED
					};
				dispatch(createTemplateFromDocumentFailedAction);

				return Promise.reject(error);
			}
		},
	resetTemplateList: () => {
		return (dispatch: Dispatch): ResetTemplatesListAction => {
			const documentTemplatesResetAction: ResetTemplatesListAction = {
				type: TemplatesActionTypes.RESET_TEMPLATES_LIST
			};
			return dispatch(documentTemplatesResetAction);
		};
	},
	resetTemplateDetails: () => {
		return (dispatch: Dispatch): ResetTemplateDetailsAction => {
			const documentTemplatesResetAction: ResetTemplateDetailsAction = {
				type: TemplatesActionTypes.RESET_TEMPLATE_DETAILS
			};
			return dispatch(documentTemplatesResetAction);
		};
	},
	addBlockToList: (
		updateBlockListSequenceRequest: UpdateBlockListSequenceRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateTemplateDetailsSuccessfulAction> => {
			const blockPlaceholder =
				LOADING_BLOCK_PLACEHOLDER + "::" + Date.now();
			const { draggableId, index, isQuoteBlockCompact } =
				updateBlockListSequenceRequest;

			try {
				let currentTemplate =
					getState().templates.currentActiveTemplate;
				const companyLabels =
					getState().user.userCompany.settings
						.pricing_table_default_label;

				if (!currentTemplate) {
					throw new Error("There is no active Template");
				}
				const templateId = currentTemplate.id;

				// showing loader in the document
				let latestBlockList = currentTemplate.block_list;
				latestBlockList.splice(index, 0, blockPlaceholder);
				const updateCurrentTemplateRequestedAction: UpdateTemplateDetailsRequestedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_REQUESTED
					};
				dispatch(updateCurrentTemplateRequestedAction);

				// creating the block
				const createBlockRequestedAction: CreateBlockRequestedAction = {
					type: BlocksActionTypes.CREATE_BLOCK_REQUESTED
				};
				proposalBuilderStore.dispatch(createBlockRequestedAction);
				const blockType = draggableIdToBlockTypeMapper[draggableId];
				let blockDataRequest = createNewBlock(
					blockType,
					undefined,
					undefined,
					undefined,
					undefined,
					isQuoteBlockCompact
				);

				if (blockType === BlockType.QUOTE) {
					blockDataRequest = {
						...blockDataRequest,
						data: {
							...blockDataRequest.data,
							label: companyLabels ? { ...companyLabels } : null
						}
					};
				}

				const blockDataResponse = await addBlock(blockDataRequest);
				const { payment_summary: paymentSummary, ...blockData } =
					blockDataResponse;
				// updating the revision with the new block id and data
				const createBlockSuccessfulAction: CreateBlockSuccessfulAction =
					{
						type: BlocksActionTypes.CREATE_BLOCK_SUCCESSFUL,
						blockData: blockData
					};
				const updateCurrentDocPaymentSummarySuccessfulAction: UpdateCurrentDocPaymentSummarySuccessfulAction =
					{
						type: DocumentsActionTypes.UPDATE_CURRENT_DOC_PAYMENT_SUMMARY_SUCCESSFUL,
						paymentSummary
					};
				dispatch(updateCurrentDocPaymentSummarySuccessfulAction);
				proposalBuilderStore.dispatch(createBlockSuccessfulAction);

				const newBlockId = blockDataResponse.id;
				currentTemplate = getState().templates.currentActiveTemplate;
				if (!currentTemplate) {
					throw new Error("There is no active Template");
				}

				latestBlockList = currentTemplate.block_list;
				const placeholderIndex =
					latestBlockList.indexOf(blockPlaceholder);
				latestBlockList.splice(placeholderIndex, 1, newBlockId);
				currentTemplate.block_list = latestBlockList;

				updateTemplateBlockList({
					templateId,
					blockList: latestBlockList
				});

				const updatingTemplateWithNewBlockId: UpdateTemplateDetailsSuccessfulAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
						currentActiveTemplate: currentTemplate
					};

				return dispatch(updatingTemplateWithNewBlockId);
			} catch (error) {
				const updateTemplateDetailsFailedAction: UpdateTemplateDetailsFailedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_FAILED
					};
				dispatch(updateTemplateDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	linkBlockListToTemplate: (
		updateBlockListSequenceRequest: UpdateMultipleBlockListSequenceRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateTemplateDetailsSuccessfulAction> => {
			const { blockList, index } = updateBlockListSequenceRequest;
			const blockIdList = blockList?.map(
				(block: BlockResponse) => block.id
			);

			try {
				const currentTemplate =
					getState().templates.currentActiveTemplate;

				if (!currentTemplate) {
					throw new Error("There is no active Template");
				}
				const templateId = currentTemplate.id;

				// showing loader in the document
				const latestBlockList = currentTemplate.block_list;
				latestBlockList.splice(index, 0, ...blockIdList);
				const updateCurrentTemplateRequestedAction: UpdateTemplateDetailsRequestedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_REQUESTED
					};
				dispatch(updateCurrentTemplateRequestedAction);

				updateTemplateBlockList({
					templateId,
					blockList: latestBlockList
				});

				const blockMapper = blockList.reduce(
					(tempObj: BlockMapper, blockData: BlockResponse) => ({
						...tempObj,
						[blockData.id]: blockData
					}),
					{}
				);

				const updateBlockMapperSuccessfulAction = {
					type: BlocksActionTypes.UPDATE_BLOCK_MAPPER_SUCCESSFUL,
					blockMapper
				};

				proposalBuilderStore.dispatch(
					updateBlockMapperSuccessfulAction
				);

				const updatingTemplateWithNewBlockId: UpdateTemplateDetailsSuccessfulAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
						currentActiveTemplate: currentTemplate
					};

				return dispatch(updatingTemplateWithNewBlockId);
			} catch (error) {
				const updateTemplateDetailsFailedAction: UpdateTemplateDetailsFailedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_FAILED
					};
				dispatch(updateTemplateDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteBlockFromRevision: ({ blockIds }: { blockIds: string[] }) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateTemplateDetailsSuccessfulAction> => {
			try {
				const updateTemplateDetailsRequestedAction: UpdateTemplateDetailsRequestedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_REQUESTED
					};
				dispatch(updateTemplateDetailsRequestedAction);

				// updating the document revision
				const currentTemplate =
					getState().templates.currentActiveTemplate;
				if (!currentTemplate) {
					throw new Error("There is no active document");
				}
				let latestBlockListIds =
					currentTemplate.block_list || new Array<string>();
				latestBlockListIds = latestBlockListIds.filter(
					id => !blockIds.includes(id)
				);
				currentTemplate.block_list = latestBlockListIds;

				// making http calls
				await updateTemplateBlockList({
					templateId: currentTemplate.id,
					blockList: latestBlockListIds
				});

				// updating the state
				const updateDocumentRevisionBlockListAction: UpdateTemplateDetailsSuccessfulAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
						currentActiveTemplate: currentTemplate
					};
				return dispatch(updateDocumentRevisionBlockListAction);
			} catch (error) {
				console.error(error);
				const updateTemplateDetailsFailedAction: UpdateTemplateDetailsFailedAction =
					{
						type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_FAILED
					};
				dispatch(updateTemplateDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	repositionBlockInRevision:
		(repositionRevisionRequest: BlockRepositionRequest) =>
		(
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateTemplateDetailsSuccessfulAction => {
			const { sourceIndex, destinationIndex } = repositionRevisionRequest;
			const currentTemplate = getState().templates.currentActiveTemplate;
			if (!currentTemplate) {
				throw new Error("There is no active Template");
			}

			const latestBlockList = currentTemplate.block_list;
			latestBlockList.splice(
				destinationIndex,
				0,
				latestBlockList.splice(sourceIndex, 1)[0]
			);
			currentTemplate.block_list = latestBlockList;

			const updatingDocumentWithNewBlockId: UpdateTemplateDetailsSuccessfulAction =
				{
					type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
					currentActiveTemplate: currentTemplate
				};

			updateTemplateBlockList({
				templateId: currentTemplate.id,
				blockList: latestBlockList
			});
			return dispatch(updatingDocumentWithNewBlockId);
		},
	linkBlockToRevision:
		(updateDocumentRevisionRequest: UpdateBlockListSequenceRequest) =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateTemplateDetailsSuccessfulAction> => {
			const { draggableId, index } = updateDocumentRevisionRequest;
			const currentTemplate = getState().templates.currentActiveTemplate;
			if (!currentTemplate) {
				throw new Error("There is no active Template");
			}

			const latestBlockList = currentTemplate.block_list;
			latestBlockList.splice(index, 0, draggableId);
			currentTemplate.block_list = [...latestBlockList];

			const updatingDocumentWithNewBlockId: UpdateTemplateDetailsSuccessfulAction =
				{
					type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
					currentActiveTemplate: currentTemplate
				};

			await updateTemplateBlockList({
				templateId: currentTemplate.id,
				blockList: latestBlockList
			});
			return dispatch(updatingDocumentWithNewBlockId);
		},
	updateMergeTags: (mergeTagVariables: MergeTagVariable[]) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateMergeTagSuccessfulAction> => {
			const updateMergeTagRequestAction: UpdateMergeTagRequestedAction = {
				type: TemplatesActionTypes.UPDATE_MERGE_TAG_REQUESTED
			};
			dispatch(updateMergeTagRequestAction);
			try {
				const currentActiveTemplate =
					getState().templates.currentActiveTemplate;
				if (!currentActiveTemplate) {
					throw new Error("There is no active template");
				} else {
					const newTemplateToUpdate: Template = {
						...currentActiveTemplate,
						merge_tag_custom_variables: mergeTagVariables
					};
					const editDocumentResponse = await editTemplate(
						newTemplateToUpdate
					);
					const updateMergeTagSuccessfulAction: UpdateMergeTagSuccessfulAction =
						{
							type: TemplatesActionTypes.UPDATE_MERGE_TAG_SUCCESSFUL,
							customMergeTagVariables:
								editDocumentResponse.merge_tag_custom_variables
						};
					return dispatch(updateMergeTagSuccessfulAction);
				}
			} catch (error) {
				const updateMergeTagFailedAction: UpdateMergeTagFailedAction = {
					type: TemplatesActionTypes.UPDATE_MERGE_TAG_FAILED
				};
				dispatch(updateMergeTagFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateSignatureAssignee: (userDetails: AssignUserToSignatureBlock) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateSignatureAssigneeSuccessfulAction> => {
			const updateSignatureAssigneeRequestedAction: UpdateSignatureAssigneeRequestedAction =
				{
					type: TemplatesActionTypes.UPDATE_SIGNATURE_ASSIGNEE_REQUESTED
				};
			dispatch(updateSignatureAssigneeRequestedAction);
			try {
				const response = await updateSignatureAssignee(userDetails);
				const updateSignatureAssigneeSuccessfulAction: UpdateSignatureAssigneeSuccessfulAction =
					{
						type: TemplatesActionTypes.UPDATE_SIGNATURE_ASSIGNEE_SUCCESSFUL,
						response: response
					};
				return dispatch(updateSignatureAssigneeSuccessfulAction);
			} catch (error) {
				const updateSignatureAssigneeFailedAction: UpdateSignatureAssigneeFailedAction =
					{
						type: TemplatesActionTypes.UPDATE_SIGNATURE_ASSIGNEE_FAILED
					};
				dispatch(updateSignatureAssigneeFailedAction);

				return Promise.reject(error);
			}
		};
	},
	openTemplateSidePanel: () => {
		return (dispatch: Dispatch): OpenTemplateSidePaneAction => {
			const openDocumentSidePaneAction: OpenTemplateSidePaneAction = {
				type: TemplatesActionTypes.OPEN_TEMPLATE_SIDE_PANEL
			};
			return dispatch(openDocumentSidePaneAction);
		};
	},
	closeTemplateSidePanel: () => {
		return (dispatch: Dispatch): CloseTemplateSidePaneAction => {
			const closeDocumentSidePaneAction: CloseTemplateSidePaneAction = {
				type: TemplatesActionTypes.CLOSE_TEMPLATE_SIDE_PANEL
			};
			return dispatch(closeDocumentSidePaneAction);
		};
	},
	updateTemplateDetails: (currentActiveTemplate: Template) => {
		return (dispatch: Dispatch): UpdateTemplateDetailsSuccessfulAction => {
			const templateDetailsUpdatedAction: UpdateTemplateDetailsSuccessfulAction = {
				type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
				currentActiveTemplate
			};
			return dispatch(templateDetailsUpdatedAction);
		};
	},
	updateBlockList:
		(blockList: string[]) =>
		async (
			dispatch: ThunkDispatch<GlobalState, any, Action>,
			getState: () => GlobalState
		): Promise<void> => {
			const currentTemplate = getState().templates.currentActiveTemplate;
			
			if (!currentTemplate) {
				throw new Error("No active template found");
			}

			try {
				const updatedTemplate = await updateTemplateBlockList({
					blockList,
					templateId: currentTemplate.id
				});

				dispatch({
					type: TemplatesActionTypes.UPDATE_TEMPLATE_DETAILS_SUCCESSFUL,
					currentActiveTemplate: updatedTemplate
				});

				const blocks = updatedTemplate.blocks?.data || [];

				const blockMapper = blocks.reduce(
					(tempObj: BlockMapper, block: Block) => ({
						...tempObj,
						[block.id]: block
					}),
					{}
				);

				const updateBlockMapperSuccessfulAction = {
					type: BlocksActionTypes.UPDATE_BLOCK_MAPPER_SUCCESSFUL,
					blockMapper
				};
				proposalBuilderStore.dispatch(updateBlockMapperSuccessfulAction);

			} catch (error) {
				console.error("Error updating template block list:", error);
				throw error;
			}
		}
};

export default TemplatesActionCreators;
